import { Box, Typography } from '@mui/material'

import { useStickToBottom } from '@/apps/latest/shared/hooks/useStickToBottom'
import { colorTokens } from '@/apps/latest/shared/styles/colorTokens'
import { UI_FONT_SIZE } from '@/shared/styles/typography'

// リアルタイム文字起こしのスクロール表示. 親の高さに fit (= caller 側で固定 height を contain).
// finals は確定行 (default 表示)、 interim は揮発の途中認識結果 (italic + muted).
// 内容更新で最下部追従、 ユーザが上にスクロールしている間は追従停止 (useStickToBottom).
//
// finals は string でも string[] でも受け付ける (records は flat、 dictated.sentences も内部で flat 化).

export interface ScrollingTranscriptProps {
  finals: string | string[]
  interim?: string
  emptyMessage?: string
  // 表示領域上限. default 'auto' (= 親 fit / 親が flex item の高さを保証する前提).
  // SoapView / HighlightList の fallback 表示では数値指定 (= caption + inline scroll).
  maxHeight?: number | string
}

export const ScrollingTranscript = ({
  finals,
  interim,
  emptyMessage = '文字起こし待ち...',
  maxHeight = '100%',
}: ScrollingTranscriptProps) => {
  const finalsArray = Array.isArray(finals)
    ? finals
    : finals
    ? finals.split('\n')
    : []
  const interimText = interim?.trim() ?? ''
  const isEmpty = finalsArray.length === 0 && !interimText

  const { ref, onScroll } = useStickToBottom<HTMLDivElement>([
    finalsArray.length,
    interimText,
  ])

  if (isEmpty) {
    return (
      <Typography
        variant="body2"
        sx={{
          color: colorTokens.fg.faint,
          fontStyle: 'italic',
        }}
      >
        {emptyMessage}
      </Typography>
    )
  }

  return (
    <Box
      ref={ref}
      onScroll={onScroll}
      sx={{
        maxHeight,
        overflow: 'auto',
        whiteSpace: 'pre-wrap',
        // Notion 教典に揃え sans (= 親フォント継承)。 旧 monospace は会話パネルから浮くため撤去。
        fontSize: UI_FONT_SIZE.label,
        lineHeight: 1.6,
        color: colorTokens.fg.default,
      }}
    >
      {finalsArray.map((line, idx) => (
        <Box key={idx} sx={{ mb: 0.5 }}>
          {line}
        </Box>
      ))}
      {interimText && (
        <Box
          sx={{
            color: colorTokens.fg.muted,
            fontStyle: 'italic',
          }}
        >
          {interimText}
        </Box>
      )}
    </Box>
  )
}
